import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MessageCircle, Send, Phone, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function MensagemBolha({ msg, index }) {
  const isCliente = msg.remetente === 'cliente';
  const isOperador = msg.remetente === 'operador';

  let bubbleClass = 'bg-white text-slate-800 rounded-bl-sm shadow';
  if (isCliente) {
    bubbleClass = 'bg-blue-600 text-white rounded-br-sm';
  } else if (isOperador) {
    bubbleClass = 'bg-green-600 text-white rounded-bl-sm';
  }

  return (
    <div className={'flex ' + (isCliente ? 'justify-end' : 'justify-start')}>
      <div className={'max-w-[80%] rounded-2xl px-4 py-3 ' + bubbleClass}>
        {!isCliente && (
          <p className={'text-xs mb-1 ' + (isOperador ? 'text-green-200' : 'text-slate-400')}>
            {msg.nome_remetente}
          </p>
        )}
        <p className="whitespace-pre-line">{msg.mensagem}</p>
      </div>
    </div>
  );
}

function OpcaoButton({ opcao, onClick, disabled }) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      className="text-xs"
      data-testid={'opcao-' + opcao.num}
    >
      {opcao.num} - {opcao.texto}
    </Button>
  );
}

export default function AtendimentoPublicoPage() {
  const [telefone, setTelefone] = useState('');
  const [atendimento, setAtendimento] = useState(null);
  const [mensagem, setMensagem] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = function() {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(function() {
    scrollToBottom();
  }, [atendimento]);

  const iniciarAtendimento = async function() {
    if (!telefone || telefone.length < 10) {
      alert('Informe um telefone válido');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(API + '/atendimento/iniciar', { telefone: telefone });
      setAtendimento(response.data.atendimento);
    } catch (error) {
      alert('Erro ao iniciar atendimento');
    }
    setLoading(false);
  };

  const enviarMensagem = async function() {
    if (!mensagem.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(
        API + '/atendimento/' + atendimento.id + '/mensagem?mensagem=' + encodeURIComponent(mensagem)
      );
      setAtendimento(response.data);
      setMensagem('');
    } catch (error) {
      alert('Erro ao enviar mensagem');
    }
    setLoading(false);
  };

  const selecionarOpcao = async function(opcao) {
    setLoading(true);
    try {
      const response = await axios.post(
        API + '/atendimento/' + atendimento.id + '/mensagem?mensagem=' + opcao
      );
      setAtendimento(response.data);
    } catch (error) {
      alert('Erro ao selecionar opção');
    }
    setLoading(false);
  };

  const novoAtendimento = function() {
    setAtendimento(null);
    setTelefone('');
    setMensagem('');
  };

  const formatarTelefone = function(value) {
    const numeros = value.replace(/\D/g, '');
    if (numeros.length <= 2) return numeros;
    if (numeros.length <= 7) return '(' + numeros.slice(0,2) + ') ' + numeros.slice(2);
    return '(' + numeros.slice(0,2) + ') ' + numeros.slice(2,7) + '-' + numeros.slice(7,11);
  };

  if (!atendimento) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4" data-testid="atendimento-publico-inicio">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-10 h-10 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">Central de Atendimento</h1>
            <p className="text-slate-500 mt-2">HOME ON</p>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Phone className="w-4 h-4 inline mr-2" />
                Seu Telefone
              </label>
              <Input
                type="tel"
                placeholder="(00) 00000-0000"
                value={telefone}
                onChange={function(e) { setTelefone(formatarTelefone(e.target.value)); }}
                className="text-lg"
                data-testid="telefone-input"
              />
            </div>
            
            <Button 
              onClick={iniciarAtendimento} 
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
              data-testid="iniciar-atendimento-btn"
            >
              {loading ? 'Iniciando...' : 'Iniciar Atendimento'}
            </Button>
          </div>
          
          <p className="text-center text-slate-400 text-sm mt-6">
            Atendimento automatizado 24h
          </p>
        </div>
      </div>
    );
  }

  const opcoesMenu = [
    { num: '1', texto: 'Suporte Técnico' },
    { num: '2', texto: '2ª Via Boleto' },
    { num: '3', texto: 'Financeiro' },
    { num: '4', texto: 'Quero ser Cliente' },
    { num: '5', texto: 'Planos TV' },
    { num: '6', texto: 'Telefonia Móvel' },
    { num: '7', texto: 'Cancelamento' },
    { num: '8', texto: 'Outros' },
    { num: '9', texto: 'Atendente' }
  ];

  const mensagensArray = atendimento.mensagens || [];

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col" data-testid="atendimento-publico-chat">
      <header className="bg-blue-600 text-white p-4 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={novoAtendimento} className="text-white hover:bg-blue-500">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-bold">Central de Atendimento</h1>
            <p className="text-blue-200 text-sm">Protocolo: {atendimento.protocolo}</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={novoAtendimento} className="text-white hover:bg-blue-500" data-testid="novo-atendimento-btn">
          Novo
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {mensagensArray.map(function(msg, index) {
          return <MensagemBolha key={index} msg={msg} index={index} />;
        })}
        <div ref={messagesEndRef} />
      </div>

      {!atendimento.categoria && (
        <div className="p-4 bg-white border-t">
          <p className="text-slate-500 text-sm mb-3 text-center">Selecione uma opção:</p>
          <div className="grid grid-cols-3 gap-2">
            {opcoesMenu.map(function(op) {
              return (
                <OpcaoButton 
                  key={op.num} 
                  opcao={op} 
                  onClick={function() { selecionarOpcao(op.num); }}
                  disabled={loading}
                />
              );
            })}
          </div>
        </div>
      )}

      <div className="p-4 bg-white border-t">
        <div className="flex gap-2">
          <Input
            placeholder="Digite sua mensagem..."
            value={mensagem}
            onChange={function(e) { setMensagem(e.target.value); }}
            onKeyPress={function(e) { if (e.key === 'Enter') enviarMensagem(); }}
            disabled={loading}
            data-testid="mensagem-input"
          />
          <Button onClick={enviarMensagem} disabled={loading || !mensagem.trim()} className="bg-blue-600" data-testid="enviar-mensagem-btn">
            <Send className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
